import "cesium/Build/Cesium/Widgets/widgets.css";
import {
  Cartesian3,
  Color,
  ImageryLayer,
  Math as CesiumMath,
  NearFarScalar,
  OpenStreetMapImageryProvider,
  PointPrimitiveCollection,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  TileMapServiceImageryProvider,
  Viewer,
  buildModuleUrl,
} from "cesium";
import "./styles.css";

const format = new Intl.NumberFormat("en-US");
const [summary, points] = await Promise.all([
  fetch("/data/atlas-summary.json").then((response) => response.json()),
  fetch("/data/atlas-points.json").then((response) => response.json()),
]);

document.querySelector("#care-count").textContent = format.format(summary.careHomes.mapReady);
document.querySelector("#organization-count").textContent = format.format(summary.animalOrganizations.osmDiscovery);
document.querySelector("#participant-count").textContent = format.format(summary.animalOrganizations.verifiedParticipants);
document.querySelector("#animal-record-count").textContent = `${format.format(summary.adoptableAnimals.records)} live records`;
document.querySelector("#candidate-count").textContent = format.format(summary.proximity.usCareHomesWithCandidateWithin100Km);

const baseLayer = ImageryLayer.fromProviderAsync(
  TileMapServiceImageryProvider.fromUrl(buildModuleUrl("Assets/Textures/NaturalEarthII"), {
    credit: "Natural Earth II",
  }),
);
baseLayer.brightness = 1.08;
baseLayer.contrast = 0.88;
baseLayer.saturation = 0.42;
baseLayer.gamma = 1.02;

const detailedMapProvider = new OpenStreetMapImageryProvider({
  url: "https://tile.openstreetmap.org/",
  maximumLevel: 19,
  credit: "© OpenStreetMap contributors",
});
detailedMapProvider.enablePickFeatures = false;
const detailedMapLayer = new ImageryLayer(detailedMapProvider);
detailedMapLayer.brightness = 1.02;
detailedMapLayer.contrast = 0.98;
detailedMapLayer.saturation = 0.72;
detailedMapLayer.gamma = 1.02;

const viewer = new Viewer("globe", {
  animation: false,
  baseLayer,
  baseLayerPicker: false,
  fullscreenButton: false,
  geocoder: false,
  homeButton: false,
  infoBox: false,
  navigationHelpButton: false,
  requestRenderMode: true,
  sceneModePicker: false,
  selectionIndicator: false,
  timeline: false,
  vrButton: false,
});

viewer.imageryLayers.add(detailedMapLayer);
viewer.resolutionScale = Math.min(window.devicePixelRatio || 1, 2);

viewer.scene.backgroundColor = Color.fromCssColorString("#dce9e8");
viewer.scene.globe.baseColor = Color.fromCssColorString("#b8d3cd");
viewer.scene.globe.enableLighting = false;
viewer.scene.globe.maximumScreenSpaceError = 1.25;
viewer.scene.globe.showGroundAtmosphere = true;
viewer.scene.fog.enabled = true;
viewer.scene.fog.density = 0.00012;
viewer.scene.skyBox.show = false;
viewer.scene.sun.show = false;
viewer.scene.moon.show = false;
viewer.scene.screenSpaceCameraController.minimumZoomDistance = 120_000;
viewer.scene.screenSpaceCameraController.maximumZoomDistance = 28_000_000;

const careCollection = viewer.scene.primitives.add(new PointPrimitiveCollection());
const organizationCollection = viewer.scene.primitives.add(new PointPrimitiveCollection());
const careColor = Color.fromCssColorString("#bf5f6b").withAlpha(0.86);
const organizationColor = Color.fromCssColorString("#217f83").withAlpha(0.88);
const careOutline = Color.fromCssColorString("#fff8f1");
const organizationOutline = Color.fromCssColorString("#e8fbfa");
const careById = new Map();
const organizationById = new Map();
const carePrimitiveById = new Map();
const organizationPrimitiveById = new Map();
const careEntries = [];
const organizationEntries = [];

for (const row of points.careHomes) {
  const record = {
    id: row[0], name: row[1], country: row[2], region: row[3], beds: row[4],
    seniorRelevance: row[5], longitude: row[6], latitude: row[7],
  };
  careById.set(record.id, record);
  const primitive = careCollection.add({
    id: { type: "care-home", record },
    position: Cartesian3.fromDegrees(record.longitude, record.latitude, 100),
    color: careColor,
    outlineColor: careOutline,
    outlineWidth: 1,
    pixelSize: 5,
    scaleByDistance: new NearFarScalar(500_000, 1.25, 20_000_000, 0.55),
  });
  carePrimitiveById.set(record.id, primitive);
  careEntries.push({ record, primitive });
}

for (const row of points.animalOrganizations) {
  const record = {
    id: row[0], name: row[1], countryCode: row[2], hasWebsite: row[3], hasPhone: row[4],
    longitude: row[5], latitude: row[6],
  };
  organizationById.set(record.id, record);
  const primitive = organizationCollection.add({
    id: { type: "animal-organization", record },
    position: Cartesian3.fromDegrees(record.longitude, record.latitude, 250),
    color: organizationColor,
    outlineColor: organizationOutline,
    outlineWidth: 1,
    pixelSize: 6,
    scaleByDistance: new NearFarScalar(500_000, 1.2, 20_000_000, 0.6),
  });
  organizationPrimitiveById.set(record.id, primitive);
  organizationEntries.push({ record, primitive });
}

const detailType = document.querySelector("#detail-type");
const detailTitle = document.querySelector("#detail-title");
const detailSummary = document.querySelector("#detail-summary");
const detailList = document.querySelector("#detail-list");
const detailAvatar = document.querySelector("#detail-avatar");
const detailStatus = document.querySelector("#detail-status");
const detailPanel = document.querySelector(".detail-panel");
const recordDisclosure = document.querySelector("#record-disclosure");
const matchPanel = document.querySelector("#match-panel");
const matchCount = document.querySelector("#match-count");
const matchSummary = document.querySelector("#match-summary");
const matchList = document.querySelector("#match-list");
let candidateIndexPromise;
let selectionSequence = 0;
let highlightedPrimitive = null;

function createIcon(symbol, className = "icon") {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
  svg.setAttribute("class", className);
  svg.setAttribute("aria-hidden", "true");
  use.setAttribute("href", `#icon-${symbol}`);
  svg.append(use);
  return svg;
}

function animateProfile() {
  detailPanel.classList.remove("is-updating");
  requestAnimationFrame(() => detailPanel.classList.add("is-updating"));
}

function setProfileAvatar(type) {
  const isCare = type === "care";
  detailAvatar.className = `profile-avatar ${isCare ? "is-care" : "is-organization"}`;
  detailAvatar.replaceChildren(createIcon(isCare ? "heart" : "paw", `icon${isCare ? "" : " icon-filled"}`));
}

function highlightPrimitive(primitive) {
  if (highlightedPrimitive && !highlightedPrimitive.isDestroyed?.()) {
    highlightedPrimitive.outlineWidth = 1;
  }
  highlightedPrimitive = primitive || null;
  if (highlightedPrimitive) highlightedPrimitive.outlineWidth = 4;
  viewer.scene.requestRender();
}

function renderDefinitionList(entries) {
  detailList.replaceChildren(...entries.map(([term, description]) => {
    const row = document.createElement("div");
    const dt = document.createElement("dt");
    const dd = document.createElement("dd");
    dt.textContent = term;
    dd.textContent = description;
    row.append(dt, dd);
    return row;
  }));
}

function loadCandidateIndex() {
  if (!candidateIndexPromise) {
    candidateIndexPromise = fetch("/data/atlas-candidates-us.json")
      .then((response) => {
        if (!response.ok) throw new Error(`Candidate data request failed with ${response.status}`);
        return response.json();
      })
      .then((payload) => new Map(payload.careHomes));
  }
  return candidateIndexPromise;
}

function flyToRecord(record) {
  viewer.camera.flyTo({
    destination: Cartesian3.fromDegrees(record.longitude, record.latitude, 190_000),
    orientation: { heading: 0, pitch: CesiumMath.toRadians(-90), roll: 0 },
    duration: 0.9,
  });
}

function contactCoverage(record) {
  const tags = [];
  if (record.hasWebsite) tags.push("website");
  if (record.hasPhone) tags.push("phone");
  return tags.length ? `${tags.join(" + ")} tagged` : "no public contact tags";
}

function makeCandidateButton(record, distanceKm) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "match-card";
  button.setAttribute("aria-label", `Open ${record.name || "unnamed mapped shelter"}, ${distanceKm} kilometers away`);

  const copy = document.createElement("span");
  copy.className = "match-card-copy";
  const name = document.createElement("strong");
  name.textContent = record.name || "Unnamed mapped shelter";
  const metadata = document.createElement("span");
  metadata.textContent = contactCoverage(record);
  copy.append(name, metadata);

  const distance = document.createElement("span");
  distance.className = "match-distance";
  distance.textContent = `${distanceKm.toFixed(1)} km`;
  const arrow = createIcon("arrow", "icon match-arrow");
  button.append(copy, distance, arrow);
  button.addEventListener("click", () => {
    renderDetails({ type: "animal-organization", record });
    highlightPrimitive(organizationPrimitiveById.get(record.id));
    flyToRecord(record);
  });
  return button;
}

async function renderCandidateMatches(record, sequence) {
  matchPanel.hidden = false;
  matchPanel.open = true;
  matchList.replaceChildren();

  if (record.country !== "US") {
    matchCount.textContent = "U.S. data only";
    matchSummary.textContent = "Validated distance-candidate sets currently cover U.S. CMS nursing-home records. This profile has no published candidate set yet.";
    return;
  }

  matchCount.textContent = "Loading";
  matchSummary.textContent = "Finding the nearest shelter-discovery records within 100 km…";

  try {
    const candidateIndex = await loadCandidateIndex();
    if (sequence !== selectionSequence) return;
    const candidates = candidateIndex.get(record.id) || [];
    matchCount.textContent = `${candidates.length} lead${candidates.length === 1 ? "" : "s"}`;
    if (!candidates.length) {
      matchSummary.textContent = "No shelter-discovery record was found within 100 km in the current public dataset. This does not prove that no shelter exists.";
      return;
    }
    matchSummary.textContent = "Nearest public discovery records, ordered only by straight-line distance. Select one to inspect its profile on the globe.";
    matchList.replaceChildren(...candidates.flatMap(([organizationId, distanceKm], index) => {
      const candidate = organizationById.get(organizationId);
      if (!candidate) return [];
      const button = makeCandidateButton(candidate, distanceKm);
      button.style.animationDelay = `${index * 35}ms`;
      return [button];
    }));
  } catch (error) {
    if (sequence !== selectionSequence) return;
    matchCount.textContent = "Unavailable";
    matchSummary.textContent = "Candidate data could not be loaded. The map remains available, and no match status has been inferred.";
    console.error(error);
  }
}

function renderDetails(selection) {
  const sequence = ++selectionSequence;
  recordDisclosure.hidden = false;
  recordDisclosure.open = false;
  animateProfile();
  if (selection.type === "care-home") {
    const record = selection.record;
    detailType.textContent = "Care-home profile";
    detailTitle.textContent = record.name;
    setProfileAvatar("care");
    detailStatus.textContent = "Research record · participation unverified";
    detailStatus.className = "profile-status is-care";
    detailSummary.textContent = "A regulator-derived facility profile. Use the nearby leads for outreach research only; participation, resident consent, and visit approval have not been established.";
    renderDefinitionList([
      ["Jurisdiction", record.country],
      ["Region", record.region || "Not supplied"],
      ["Certified beds", record.beds == null ? "Not supplied" : format.format(record.beds)],
      ["Senior relevance", record.seniorRelevance.replaceAll("-", " ")],
      ["Source record", record.id],
    ]);
    renderCandidateMatches(record, sequence);
  } else {
    const record = selection.record;
    detailType.textContent = "Animal-organization profile";
    detailTitle.textContent = record.name || "Unnamed mapped shelter";
    setProfileAvatar("organization");
    detailStatus.textContent = "Discovery only · direct verification required";
    detailStatus.className = "profile-status is-organization";
    detailSummary.textContent = "An OpenStreetMap discovery profile. Current operation, animal availability, suitability, and willingness to participate all require direct confirmation.";
    renderDefinitionList([
      ["Country tag", record.countryCode || "Not supplied"],
      ["Website tagged", record.hasWebsite ? "Yes" : "No"],
      ["Phone tagged", record.hasPhone ? "Yes" : "No"],
      ["Contact coverage", contactCoverage(record)],
      ["Source record", record.id],
    ]);
    matchPanel.hidden = true;
    matchPanel.open = false;
    matchList.replaceChildren();
  }
}

const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);
handler.setInputAction((movement) => {
  const picked = viewer.scene.pick(movement.position);
  const selection = picked?.id || picked?.primitive?.id;
  if (selection?.record) {
    const primitive = selection.type === "care-home"
      ? carePrimitiveById.get(selection.record.id)
      : organizationPrimitiveById.get(selection.record.id);
    highlightPrimitive(primitive);
    renderDetails(selection);
  }
}, ScreenSpaceEventType.LEFT_CLICK);

const views = {
  world: [0, 12, 24_000_000],
  us: [-98, 38, 5_800_000],
  england: [-1.5, 52.7, 1_050_000],
  scotland: [-4.2, 56.7, 1_150_000],
};
function flyToView(key, duration = 1.2) {
  const [longitude, latitude, height] = views[key];
  viewer.camera.flyTo({
    destination: Cartesian3.fromDegrees(longitude, latitude, height),
    orientation: { heading: 0, pitch: CesiumMath.toRadians(-90), roll: 0 },
    duration,
  });
}

const careLayerButton = document.querySelector("#care-layer");
const organizationLayerButton = document.querySelector("#organization-layer");
const careFilter = document.querySelector("#care-filter");
const organizationFilter = document.querySelector("#organization-filter");
const filterToggle = document.querySelector("#filter-toggle");
const filterPanel = document.querySelector("#filter-panel");
const activeFilterCount = document.querySelector("#active-filter-count");
const visibleRecordCount = document.querySelector("#visible-record-count");
const viewSelect = document.querySelector("#view-select");
const careLegend = document.querySelector(".legend span:first-child");
const organizationLegend = document.querySelector(".legend span:last-child");
let careLayerVisible = true;
let organizationLayerVisible = true;

function careMatchesFilter(record) {
  if (careFilter.value === "older") return /(over-65|older|aged)/i.test(record.seniorRelevance);
  if (careFilter.value === "nursing") return /nursing/i.test(record.seniorRelevance);
  return true;
}

function organizationMatchesFilter(record) {
  if (organizationFilter.value === "both-contact") return record.hasWebsite && record.hasPhone;
  if (organizationFilter.value === "any-contact") return record.hasWebsite || record.hasPhone;
  return true;
}

function updateFilterStatus(careVisibleCount, organizationVisibleCount) {
  const filterCount = Number(careFilter.value !== "all") + Number(organizationFilter.value !== "all");
  activeFilterCount.hidden = filterCount === 0;
  activeFilterCount.textContent = String(filterCount);
  const total = careVisibleCount + organizationVisibleCount;
  visibleRecordCount.textContent = `${format.format(total)} record${total === 1 ? "" : "s"} visible`;
}

function applyFilters() {
  let careVisibleCount = 0;
  let organizationVisibleCount = 0;
  for (const { record, primitive } of careEntries) {
    primitive.show = careLayerVisible && careMatchesFilter(record);
    if (primitive.show) careVisibleCount += 1;
  }
  for (const { record, primitive } of organizationEntries) {
    primitive.show = organizationLayerVisible && organizationMatchesFilter(record);
    if (primitive.show) organizationVisibleCount += 1;
  }
  careLayerButton.classList.toggle("is-active", careLayerVisible);
  careLayerButton.setAttribute("aria-pressed", String(careLayerVisible));
  organizationLayerButton.classList.toggle("is-active", organizationLayerVisible);
  organizationLayerButton.setAttribute("aria-pressed", String(organizationLayerVisible));
  careLegend.classList.toggle("is-muted", !careLayerVisible);
  organizationLegend.classList.toggle("is-muted", !organizationLayerVisible);
  updateFilterStatus(careVisibleCount, organizationVisibleCount);
  viewer.scene.requestRender();
}

careLayerButton.addEventListener("click", () => {
  careLayerVisible = !careLayerVisible;
  applyFilters();
});
organizationLayerButton.addEventListener("click", () => {
  organizationLayerVisible = !organizationLayerVisible;
  applyFilters();
});
careFilter.addEventListener("change", applyFilters);
organizationFilter.addEventListener("change", applyFilters);
document.querySelector("#reset-filters").addEventListener("click", () => {
  careFilter.value = "all";
  organizationFilter.value = "all";
  applyFilters();
});
filterToggle.addEventListener("click", () => {
  const open = filterPanel.hidden;
  filterPanel.hidden = !open;
  filterToggle.setAttribute("aria-expanded", String(open));
  if (open) careFilter.focus();
});

viewSelect.addEventListener("change", (event) => flyToView(event.target.value));
document.querySelector("#reset-view").addEventListener("click", () => flyToView(viewSelect.value));
document.querySelector("#zoom-in").addEventListener("click", () => {
  viewer.camera.zoomIn(viewer.camera.positionCartographic.height * 0.32);
  viewer.scene.requestRender();
});
document.querySelector("#zoom-out").addEventListener("click", () => {
  viewer.camera.zoomOut(viewer.camera.positionCartographic.height * 0.45);
  viewer.scene.requestRender();
});

const themeToggle = document.querySelector("#theme-toggle");
const themeLabel = themeToggle.querySelector(".theme-label");
const themeUse = themeToggle.querySelector("use");
const themeMeta = document.querySelector('meta[name="theme-color"]');

function applyTheme(theme, persist = true) {
  const night = theme === "night";
  document.documentElement.dataset.theme = night ? "night" : "warm";
  themeToggle.setAttribute("aria-pressed", String(night));
  themeToggle.setAttribute("aria-label", `Switch to ${night ? "warm" : "night"} theme`);
  themeLabel.textContent = night ? "Warm" : "Night";
  themeUse.setAttribute("href", night ? "#icon-sun" : "#icon-moon");
  themeMeta.setAttribute("content", night ? "#101816" : "#f5f0e8");

  baseLayer.brightness = night ? 0.68 : 1.08;
  baseLayer.contrast = night ? 1.06 : 0.88;
  baseLayer.saturation = night ? 0.28 : 0.42;
  detailedMapLayer.brightness = night ? 0.7 : 1.02;
  detailedMapLayer.contrast = night ? 1.08 : 0.98;
  detailedMapLayer.saturation = night ? 0.48 : 0.72;
  viewer.scene.backgroundColor = Color.fromCssColorString(night ? "#0d1a19" : "#dce9e8");
  viewer.scene.globe.baseColor = Color.fromCssColorString(night ? "#172b28" : "#b8d3cd");

  const nextCareColor = Color.fromCssColorString(night ? "#e27b87" : "#c25f6c").withAlpha(0.9);
  const nextOrganizationColor = Color.fromCssColorString(night ? "#65c1c0" : "#237f83").withAlpha(0.9);
  const nextCareOutline = Color.fromCssColorString(night ? "#ffe5e8" : "#fff8f1");
  const nextOrganizationOutline = Color.fromCssColorString(night ? "#d7fffc" : "#e8fbfa");
  for (const { primitive } of careEntries) {
    primitive.color = nextCareColor;
    primitive.outlineColor = nextCareOutline;
  }
  for (const { primitive } of organizationEntries) {
    primitive.color = nextOrganizationColor;
    primitive.outlineColor = nextOrganizationOutline;
  }
  if (highlightedPrimitive) highlightedPrimitive.outlineWidth = 4;
  viewer.scene.requestRender();
  if (persist) {
    try { localStorage.setItem("care-atlas-theme", night ? "night" : "warm"); } catch { /* optional preference */ }
  }
}

themeToggle.addEventListener("click", () => {
  applyTheme(document.documentElement.dataset.theme === "night" ? "warm" : "night");
});

let initialTheme = "warm";
try { initialTheme = localStorage.getItem("care-atlas-theme") || "warm"; } catch { /* optional preference */ }
applyTheme(initialTheme, false);
applyFilters();

viewer.scene.globe.tileLoadProgressEvent.addEventListener((remaining) => {
  if (remaining === 0) document.querySelector("#loading")?.remove();
});
window.setTimeout(() => document.querySelector("#loading")?.remove(), 2500);
flyToView("world", 0);
